/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module LogsModule
 */

import {
    AfterViewInit,
    Component,
    ElementRef,
    Injector,
    Input,
    OnDestroy,
    OnInit,
    Type,
    ViewChild,
} from '@angular/core';

import { ConnectedPosition } from '@angular/cdk/overlay';
import { L10nService } from '@vmw/ngx-vip';
import * as globalL10n from 'global-l10n';
import { optionsPositionsPriority } from 'standalone-components/avi-dropdown';
import { DialogService } from 'ng/modules/core/services/dialog.service';
import { ILogsSearchBarStates, LogsSearchBarStore } from 'ng/modules/logs/services/logs-search-bar.store';

import {
    TrafficLogsSearchBarDropdownComponent,
} from '../traffic-logs-search-bar-dropdown/traffic-logs-search-bar-dropdown.component';

import { TrafficLogsSearchBarStore } from './traffic-logs-search-bar.store';
import { LogsSearchBarDropdownService } from '../../../services/logs-search-bar-dropdown.service';
import {
    SaveSearchType,
    TrafficLogsSaveSearchModalComponent,
} from '../traffic-logs-save-search-modal/traffic-logs-save-search-modal.component';
import { LogsFilterInputComponent } from '../../logs-filter-input/logs-filter-input.component';

import * as l10n from './traffic-logs-search-bar.l10n';
import './traffic-logs-search-bar.component.less';
import { ITrafficLogsStates, TrafficLogsStore } from '../traffic-logs.store';

const { ...globalL10nKeys } = globalL10n;
const { ENGLISH: dictionary, ...l10nKeys } = l10n;

const TRAFFIC_LOGS_SAVE_SEARCH_MODAL_ID = 'TRAFFIC_LOGS_SAVE_SEARCH_MODAL';

/**
 * @description Traffic Logs search bar component.
 * @author Yogesh Patil
 */
@Component({
    selector: 'traffic-logs-search-bar',
    templateUrl: './traffic-logs-search-bar.component.html',
    providers: [
        TrafficLogsSearchBarStore,
        {
            provide: LogsSearchBarStore<ILogsSearchBarStates>,
            useExisting: TrafficLogsSearchBarStore,
        },
    ],
})
export class TrafficLogsSearchBarComponent implements AfterViewInit, OnInit, OnDestroy {
    /**
     * The ID of the pool used to filter logs from a VS.
     */
    @Input()
    public poolId?: string;

    /**
     * Element ref for the search bar container.
     */
    @ViewChild('searchBarContainer')
    public searchBarContainerRef: ElementRef;

    /**
     * Element ref for the search icon.
     */
    @ViewChild('searchIcon')
    public searchIconRef: ElementRef;

    @ViewChild(TrafficLogsSearchBarDropdownComponent)
    private trafficLogsSearchBarDropdown: TrafficLogsSearchBarDropdownComponent;

    @ViewChild(LogsFilterInputComponent)
    private logsFilterInput: LogsFilterInputComponent;

    public readonly globalL10nKeys = globalL10nKeys;

    public readonly l10nKeys = l10nKeys;

    /**
     * Save enum as class property for access in view.
     */
    public readonly SaveSearchTypeEnum = SaveSearchType;

    /**
     * Position definition of the dropdown relative to the search bar.
     */
    public readonly dropdownPosition: ConnectedPosition[] = optionsPositionsPriority;

    /**
     * Calculated width of the dropdown.
     */
    public dropdownWidth: number;

    /**
     * Observer of search bar to report changes in size.
     */
    private searchBarResizeObserver: ResizeObserver;

    constructor(
        l10nService: L10nService,
        protected readonly trafficLogsStore: TrafficLogsStore<ITrafficLogsStates>,
        public readonly trafficLogsSearchBarStore: TrafficLogsSearchBarStore,
        public readonly logsSearchBarDropdownService: LogsSearchBarDropdownService,
        private readonly dialogService: DialogService,
        private readonly injector: Injector,
    ) {
        l10nService.registerSourceBundles(dictionary);
    }

    /** @override */
    public ngOnInit(): void {
        if (this.poolId) {
            this.trafficLogsSearchBarStore.setPoolIdForRequest(this.poolId);
        }
    }

    /** @override */
    public ngAfterViewInit(): void {
        // init search bar resize observer & callback
        this.searchBarResizeObserver = new ResizeObserver(this.setDropdownWidth);
        this.searchBarResizeObserver.observe(document.querySelector('.traffic-logs-search-bar'));
    }

    /** @override */
    public ngOnDestroy(): void {
        this.searchBarResizeObserver.disconnect();
    }

    /**
     * Calculate and set width of dropdown.
     */
    public setDropdownWidth = (): void => {
        /* eslint-disable no-unsafe-optional-chaining */
        this.dropdownWidth = this.searchBarContainerRef?.nativeElement.offsetWidth -
            this.searchIconRef?.nativeElement.offsetWidth;
        /* eslint-enable no-unsafe-optional-chaining */
    };

    /**
     * Callback to ngFor-track by.
     */
    // eslint-disable-next-line class-methods-use-this
    public trackByIndex(index: number): number {
        return index;
    }

    /**
     * Open the save search modal.
     */
    public openSaveSearchModal(type: SaveSearchType): void {
        this.dialogService.add({
            id: TRAFFIC_LOGS_SAVE_SEARCH_MODAL_ID,
            component: TrafficLogsSaveSearchModalComponent as Type<Component>,
            componentProps: {
                saveSearchType: type,
                onClose: () => this.dialogService.remove(TRAFFIC_LOGS_SAVE_SEARCH_MODAL_ID),
            },
            injector: this.injector,
        });
    }

    /**
     * Handle keydown event from search input. Used for adding keyboard controls to the dropdown.
     */
    public handleSearchKeydown(event: KeyboardEvent): void {
        switch (event.key) {
            case 'ArrowDown':
                this.trafficLogsSearchBarDropdown.focusDropdownOptions();
                break;
            case 'Enter':
            case 'Tab':
                this.trafficLogsSearchBarStore.addFilter();
                this.logsSearchBarDropdownService.openDropdown();
                break;
        }
    }

    /**
     * Handle escaping dropdown options. Used for adding keyboard controls to the dropdown.
     */
    public handleEscapeDropdownOptions(): void {
        this.logsFilterInput.refocus();
    }
}
